import { useNavigate, useParams } from "react-router-dom";
import { useTranslate } from "@tolgee/react";
import { useMemo, useCallback, useState } from "react";
import { NotesList } from "./NotesList";
import Berries from "../../assets/berries.svg?react";
import Leaves from "../../assets/leaves.svg?react";
import CheckmarkCircle from "../../assets/checkmark-circle.svg?react";
import CrossCircle from "../../assets/cross-circle.svg?react";
import {
  ActivityStatus,
  useVisitByIdQuery,
  useClockOutMutation,
} from "../../api/generated/graphql";
import { useCareContext } from "../../providers";
import { Headline, Label, Paragraph } from "@frontend/lyng/typography";
import { match } from "ts-pattern";
import { Button } from "@frontend/lyng/button";
import { useVisitInfo } from "../../utils/visits";
import { useGeoLocation } from "../../hooks/geoLocation";
import toast from "react-hot-toast";
import ShiftThankYouSlideOver from "./ShiftThankYou";
import classNames from "classnames";

export type CompleteShiftActivity = {
  id: string;
  title: string;
  description: string;
  isCompleted: boolean;
  isDue: boolean;
  subjectName?: string;
  statusNote?: string;
  status: "completed" | "not_completed";
};

export type CompleteShiftNote = {
  id: string;
  createdAt: string;
  note: string;
  authorName: string;
  careRecipientName?: string;
  isMine: boolean;
};

export type CompleteShiftProps = {
  activities?: CompleteShiftActivity[];
  notes?: CompleteShiftNote[];
  userId?: string;
  onConfirm?: () => void;
  onBack?: () => void;
  loading?: boolean;
};

const EmptyScreen = ({ message }: { message: string }) => (
  <div className="flex flex-col gap-8 px-2 pt-10 items-center justify-center ">
    <div className="flex flex-row items-center justify-between w-2/3 px-8">
      <Berries className="h-16 w-16" />
      <Leaves className="h-16 w-16" />
    </div>
    <Paragraph size="m">{message}</Paragraph>
  </div>
);

type SummaryShiftActivityCardProps = {
  activity: {
    id: string;
    status: ActivityStatus | null;
    description: string;
    comment: string | null;
    title: string;
  };
  isLast?: boolean;
};

const SummaryShiftActivityCard = ({
  activity,
  isLast,
}: SummaryShiftActivityCardProps) => {
  const cardHeader = useMemo(
    () =>
      match({ status: activity.status, title: activity.title })
        .with({ status: ActivityStatus.Completed }, ({ title }) => (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-secondary-700 rounded-xl flex items-center justify-center aspect-square">
              <CheckmarkCircle className="w-4 h-4 object-contain" />
            </div>
            <Label size="xs">{title}</Label>
          </div>
        ))
        .with({ status: ActivityStatus.CouldNotComplete }, ({ title }) => (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-critical-900 rounded-xl flex items-center justify-center">
              <CrossCircle className="w-4 h-4" />
            </div>
            <Label size="xs">{title}</Label>
          </div>
        ))
        .otherwise(() => null),
    [activity.status, activity.title],
  );

  return (
    <div
      key={activity.id}
      className={classNames("bg-greyscale-900 px-4 py-2 flex flex-col", {
        "border-b border-greyscale-700": !isLast,
      })}
    >
      {cardHeader}
      {activity.comment && activity.comment.trim() !== "" && (
        <div className="mt-3 bg-greyscale-800 rounded-xl px-3 py-4 text-greyscale-100 text-sm">
          <Paragraph size="s">{activity.comment}</Paragraph>
        </div>
      )}
    </div>
  );
};

const CompleteShift = ({ onConfirm, onBack }: CompleteShiftProps) => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    state: { viewer },
  } = useCareContext();

  const { data, loading } = useVisitByIdQuery({
    variables: {
      visitInstanceId: id ?? "",
    },
  });

  const visitInfo = useVisitInfo(data?.visitById);

  const allActivities = useMemo(
    () => visitInfo?.activities ?? [],
    [visitInfo?.activities],
  );

  const dueOrCompletedActivities = useMemo(
    () =>
      allActivities.filter(
        (a) => a.isDue || a.status !== ActivityStatus.NotStarted,
      ),
    [allActivities],
  );

  const completedByMe = useMemo(
    () =>
      dueOrCompletedActivities.filter((a) => a.completedBy?.id === viewer?.id),
    [dueOrCompletedActivities, viewer?.id],
  );

  const myNotes = useMemo(
    () =>
      (data?.visitById?.visitNotes || []).filter(
        (n) => n.author?.id === viewer?.id,
      ),
    [data?.visitById?.visitNotes, viewer?.id],
  );

  const noDueActivities = useMemo(
    () => dueOrCompletedActivities.length === 0,
    [dueOrCompletedActivities],
  );
  const noMyCompletedActivities = useMemo(
    () => completedByMe.length === 0,
    [completedByMe],
  );
  const noMyNotes = useMemo(() => myNotes.length === 0, [myNotes]);

  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [clockOutMutation] = useClockOutMutation();
  const [clockOutLoading, setClockOutLoading] = useState(false);
  const { getPosition } = useGeoLocation();

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  }, [onBack, navigate]);

  const handleConfirm = useCallback(async () => {
    if (onConfirm) {
      onConfirm();
      return;
    }

    if (!id) {
      toast.error(t("completeShift.error.noVisitId"));
      return;
    }

    setClockOutLoading(true);

    try {
      const position = await getPosition();
      const coords = position?.coords;
      const location =
        coords?.latitude && coords.longitude
          ? {
              lat: coords.latitude,
              lng: coords.longitude,
            }
          : undefined;

      const input = {
        visitInstanceId: id,
        visitNote: "", // Empty note since this is just completing the shift
        clockOutLocation: location,
        clockOutTime: new Date().toISOString(),
        expenses: [],
        mileage: undefined,
      };

      await clockOutMutation({
        variables: { input },
        refetchQueries: ["VisitById"],
      });

      setShowThankYouModal(true);
    } catch (error) {
      console.error("Clock out error:", error);
      toast.error(t("completeShift.error.clockOutFailed"));
    } finally {
      setClockOutLoading(false);
    }
  }, [onConfirm, id, getPosition, clockOutMutation, t]);

  if (noDueActivities && noMyNotes) {
    return <EmptyScreen message={t("completeShift.nothingToShow")} />;
  }

  if (!noDueActivities && noMyCompletedActivities && noMyNotes) {
    return <EmptyScreen message={t("completeShift.noActivitiesCompleted")} />;
  }

  if (loading || !data) {
    return <span>Loading shift data</span>;
  }

  return (
    <>
      <div className="flex flex-col h-screen bg-main">
        <div className="flex-1 overflow-y-auto">
          {!noDueActivities && (
            <>
              <Headline size="m" className="pt-10 pb-3 px-4">
                {t("completeShift.activities")}
              </Headline>
              {noMyCompletedActivities ? (
                <div className="flex flex-col items-center justify-center py-10">
                  <Paragraph size="m">
                    {t("completeShift.noActivitiesCompleted")}
                  </Paragraph>
                </div>
              ) : (
                <div className="flex flex-col gap-4 pt-3 px-4">
                  {completedByMe.map((activity, idx) => (
                    <SummaryShiftActivityCard
                      key={activity.id}
                      activity={activity}
                      isLast={idx === completedByMe.length - 1}
                    />
                  ))}
                </div>
              )}
            </>
          )}
          {!noMyNotes && (
            <div className="flex flex-col">
              <Headline size="m" className="pt-12 px-4">
                {t("completeShift.notes")}
              </Headline>
              <div className="px-2">
                <NotesList
                  notes={myNotes}
                  hasClockedIn={true}
                  displayView={true}
                />
              </div>
            </div>
          )}
        </div>
        <div className="fixed bottom-0 w-full bg-main z-30">
          <div className="flex flex-row justify-between gap-2 px-4 pt-2 pb-8 bg-greyscale-800">
            <Button
              variant="secondary"
              text={t("completeShift.back")}
              className="flex-1"
              onClick={handleBack}
              disabled={clockOutLoading}
            />
            <Button
              variant="primary"
              text={t("completeShift.confirm")}
              className="flex-1"
              onClick={handleConfirm}
              loading={clockOutLoading}
            />
          </div>
        </div>
      </div>
      {showThankYouModal && (
        <ShiftThankYouSlideOver onClose={() => navigate("/")} />
      )}
    </>
  );
};

export default CompleteShift;
